// Blog post data. Static so the site stays a pure Cloudflare Pages deploy
// with no backend dependency. Add new posts to the top of the array.

export const categories = ["Case Study", "Business Highlight", "Playbook"];

const posts = [
  {
    slug: "marketing-agency-matt-appsumo-90m",
    title: "How a guy named Matt took AppSumo from $7M to $90M",
    excerpt:
      "We spent years trying to grow the pie. Vanity metrics everywhere. Then we found one person and built the whole company around him.",
    category: "Case Study",
    author: "Nick Christensen",
    authorRole: "Head of Growth, AppSumo",
    publishedAt: "April 25, 2026",
    readTime: 6,
    coverGradient: "from-[#D4FF4A]/20 via-[#D4FF4A]/5 to-transparent",
    coverEyebrow: "Case study · AppSumo",
    coverHeadline: "$7M → $90M",
    tags: ["icp", "appsumo", "agency", "ltv", "cac"],
    content: `## We were chasing the wrong number

For years at AppSumo we were trying to grow the pie. More buyers. More traffic. More everything. We had aggressive acquisition goals so honestly it was just easier to blast our marketing to as many people as possible.

But deep down I knew we were chasing vanity metrics. When I really dug into the data it was eye opening.

10% of our buyers were driving about half our revenue. Not because they spent a little more. The best buyers were coming back 10, 20, even 50 times to purchase. They became Plus members. They stuck around for years.

Most of my ad spend was optimized to bring in anyone. But "anyone" has terrible LTV.

## The shift from CAC to LTV:CAC

The real shift for me was moving from CAC to LTV:CAC. CAC dropping means nothing if the customers you're acquiring don't come back.

I've seen campaigns with $200 CAC outperform campaigns with $75 CAC because the $200 buyers had 4x the lifetime value. The $75 ones bought once and left. The $200 ones became Plus members and bought again and again.

That changed everything about how we evaluated channels.

## Meet Matt

Once we knew the 10% existed, we wanted to know who they actually were.

We dug in. We looked at job titles, company sizes, what they bought, what order, what they said in support tickets, where they hung out online. A pattern showed up that we hadn't seen before.

It was a one-person marketing agency. Maybe a couple of contractors. Servicing 5 to 20 clients. Running paid, doing SEO, building landing pages, doing content. Everything for everyone.

We named him Matt.

Marketing Agency Matt.

## Why Matt loved AppSumo

Once you can see Matt clearly, his behavior makes total sense. Lifetime deals are a cheat code for an agency.

Overhead kills agencies. The whole game is keeping costs low and margins high. A SaaS subscription bill that grows every month is the enemy. A one-time payment for a tool you'll use forever is the dream.

Matt would buy a landing page builder, a CRM, an SEO tool, an email tool, a social scheduler, all on lifetime deals. Suddenly he's running an agency stack that costs him $0 a month after the initial spend. He's punching way above his weight. One person, looks like a 20-person shop.

Then he repackages those tools to his clients. He sells them as part of a retainer. He's saving money AND making money on the same tools.

That's why Matt would buy 30 times in a year. The math just works.

## What we did about it

This is the part that took us from $7M to $90M. Once we knew it was Matt, we built the whole thing around him.

**Product.** We built code stacking. The more codes you stack on a single product, the more features unlock. That alone drove AOV through the roof. Matt was already buying multiple, now he had a reason to buy more of the same one.

**Copy.** Every piece of marketing started speaking agency. White label. Retainer. Margin. Client deliverables. The language had to feel like it was written for him.

**Curation.** We started favoring tools that were agency-friendly in our weekly drops. Founders pitching us would get more weight if they could speak to the agency use case.

**Email.** We segmented Matt out. He got different emails. Different deals. Different stacking suggestions. We treated him like the most valuable customer in the building, because he was.

The compounding effect of all of this was the move from $7M to $90M. Not one big swing. A hundred small decisions all pointing at the same person.

## What I'd tell my past self

The lesson I keep coming back to is this: stop trying to grow the pie. Find your 10% and build for them. Let everybody else self-select in or out.

If your marketing is trying to talk to everyone, it's talking to no one. The brands that win are the ones who pick a person and obsess over that person. Everything else flows from that.

Matt isn't unique to AppSumo. Every business has a version of him. There is a 10% somewhere in your customer base driving most of your revenue, and most companies have no idea who they are.

That's the part that's wild to me. The thing that drives the business is sitting there in the data, named, and nobody's looking at it.

That's the whole reason we built ICPIntel. To make finding your Matt as easy as importing a customer list.

Go find your 10%.`,
  },
  {
    slug: "duolingo-billion-dollar-business",
    title: "How Duolingo turned a free language app into a $1B business",
    excerpt:
      "47 million people open Duolingo every day. Around 9% of them pay. That math still gets you a billion dollars in revenue and a $7.7B valuation. Here's how the model actually works.",
    category: "Business Highlight",
    author: "Nick Christensen",
    authorRole: "Head of Growth, AppSumo",
    publishedAt: "April 24, 2026",
    readTime: 5,
    coverGradient: "from-[#58CC02]/30 via-[#D4FF4A]/10 to-transparent",
    coverEyebrow: "Business highlight · Duolingo",
    coverHeadline: "$1B / year",
    coverLogo: "/images/duolingo-logo.svg",
    tags: ["business model", "freemium", "icp", "ltv"],
    content: `## The headline number

Duolingo is doing roughly $1B in revenue in 2025. Up from $748M in 2024, $531M in 2023, $369M in 2022. The chart is a hockey stick that's still bending up.

47 million people use the app every day. 128 million in any given month. Around 10.9 million of them pay.

That's just under 9% paid conversion. Those 9% generate about 80% of the company's revenue. The other 91% pay nothing.

This is the same shape as the AppSumo story. The same shape as basically every business once you actually look. A small slice of users drives almost everything.

The difference with Duolingo is they built the entire business model around it on purpose.

## The free product is the funnel

Most companies treat free as a leaky top of funnel. A trial that's supposed to convert. Pressure tactics, locked features, paywalls dropped in front of you to get you to upgrade.

Duolingo runs the opposite play. The free product is the product. It's not crippled. It's not nagging. You can use Duolingo free for ten years and never hit a wall that says "pay or leave."

That sounds like a terrible business decision. It's actually the whole reason the business works.

When the free product is genuinely good, it pulls in volume that paid acquisition can't match. 128 million people a month is a number you can't buy. You earn it by being the obvious thing your friend tells you to download.

Then a fraction of those users want a slightly better experience and they pay $10 a month for it. No ads. Unlimited lives. Sometimes the AI tutor.

That fraction is small. But the top of funnel is so massive that the small fraction is a billion dollars.

## They could squeeze. They don't.

Here's the part that I think most operators miss.

Duolingo's CEO has said publicly that they could massively increase paid conversion tomorrow. Play five ads at the end of a lesson instead of one. Force a paywall after three days. Lock more features. They'd have an amazing next quarter.

And then growth would die. People would stop telling their friends. The free experience would feel like a hostage situation. The 128 million top of funnel would shrink to 50 million. The compounding would stop.

So they don't squeeze. They protect the free experience like it's the asset. Because it is.

This is the discipline most companies don't have. Quarter to quarter pressure makes you turn the screw on conversion. The Duolingo bet is that the long game is much bigger if you don't.

## They know exactly who pays

Here's where it gets interesting for an ICP nerd.

Duolingo runs something like 10,000 A/B tests. They have 13 billion exercise completions per week to feed AI. Every notification time, every message wording, every lesson length, every onboarding question, gets tested.

What that means is Duolingo knows their paying user with absurd precision. They know what time of day a Chinese learner is most likely to come back vs an English learner. They know which guilt-trip notification works best on a 32-year-old woman who hasn't opened the app in 4 days.

They built a bandit algorithm to figure out which message to send each person. Not a generic CRM segment. The actual best message for the actual person.

Most companies talk about personalization. Duolingo built a research operation that does it.

The 9% who pay aren't random. They're the users whose behavior signals patience, consistency, and willingness to pay for a small upgrade. Duolingo's whole product surface is engineered to find those users and graduate them gently into Super Duolingo or Max.

## What this means for everyone else

You probably don't have 128 million monthly users. You don't need them. The lesson isn't the scale. The lesson is the shape.

There are a few things that make Duolingo's economics work and most of them are portable.

**Free has to be genuinely good.** A neutered free product is a worse marketing channel than no free product at all. If you're going to do freemium, the free version has to be the thing your customers tell their friends about.

**Know who your 9% are.** Not "marketing managers at SMB companies." The actual signal that separates the 9% from the 91%. For Duolingo it's daily streak behavior, completion rates, specific lesson types. For your business it's something else, but it's there in the data.

**Build the whole product around graduating that 9%.** Every notification, every feature unlock, every message should be optimized for the journey from free to paid for the people who are actually going to make that journey. Don't waste cycles trying to convert the 91% who never will.

**Don't squeeze the free experience to hit a quarter.** The compounding from a healthy top of funnel beats short-term conversion every time. This is the discipline thing.

## The interesting part

The most interesting line in the whole Duolingo story is from their CFO talking about Max, the new AI tier at $30/month.

They could replace human language tutors with AI. They tried offering humans first and people said they wanted it but never used it. The reason was social. People didn't want to be judged by another human while sounding like a beginner.

The AI doesn't judge you. Even though, technically, the AI absolutely is judging you.

That's a real insight about a real customer. They went and built a $30/month product for the version of the user who wants to practice without the social cost. That's product strategy that comes from knowing the customer cold.

This is what the whole game is going to look like in five years. The companies that win won't be the ones with the most features. They'll be the ones who know their paying users with the most precision.

Duolingo just happens to be a few years ahead.`,
  },
  {
    slug: "ai-inputs-vs-outputs-marketing",
    title: "The output of AI is only as good as the input you give it",
    excerpt:
      "Most marketing teams using AI are using it backwards. The strength isn't in letting it think for you. It's in feeding it the best inputs you've ever had.",
    category: "Playbook",
    author: "Nick Christensen",
    authorRole: "Head of Growth, AppSumo",
    publishedAt: "April 23, 2026",
    readTime: 5,
    coverGradient: "from-[#D4FF4A]/25 via-[#FF6B35]/10 to-transparent",
    coverEyebrow: "Playbook · AI in marketing",
    coverHeadline: "Garbage in. Garbage out.",
    tags: ["ai", "marketing", "prompting", "skills", "inputs"],
    content: `## The output of AI is only as good as the input you give it.

Shit input, shit output. That's really the whole point.

Most marketing teams I see using AI are using it backwards. They're treating it like a copywriter. Like a designer. Like a strategist. They open a chat, type "write me a Facebook ad for my product," and ship whatever comes out.

That's lazy. And the work shows it.

## The wrong way to use AI

The wrong way to use AI in marketing is to outsource the human parts.

The thinking. The taste. The strategic call about what story you're telling. The decision about who you're talking to. The judgment about what makes this brand feel like this brand. None of that is what AI is good at. That's what you're for.

When you let AI do the thinking, the output reads like AI did the thinking. Generic. Hedged. Vaguely right. Forgettable. You can spot it from across the room.

The people losing here are the ones using AI to skip work that actually matters. Writing copy without writing the brief. Generating images without thinking about what they're trying to say. Pumping out ten variations and shipping the one that "feels okay" without ever doing the actual creative thinking.

That's not AI being bad. That's a person being lazy with a powerful tool.

## What AI is actually good at

Here's where AI absolutely smokes humans. Synthesizing huge amounts of data into a usable thing.

Take 5,000 customer transactions, surface the patterns, give me three named segments and what they have in common. AI eats that for breakfast. A human takes a week.

Take 200 customer interview transcripts, pull out the recurring language, give me the top 20 phrases people actually use to describe the problem. Same thing. Hours of work compressed to minutes.

Take six months of website analytics, cross-reference with email behavior, find the conversion path that actually works. Something a human can do but probably won't, because it's tedious. AI does it without complaining.

This is the real power. Not generating something out of nothing. Synthesizing real signal from noise that you couldn't read by hand.

## Inputs are the moat

If the output is only as good as the input, then your inputs are the actual competitive advantage.

This is the part most teams haven't internalized yet. You're not competing on prompts. Everyone has the same models. Everyone has the same chat interface. The model is a commodity. The interface is a commodity.

What's not a commodity is the data you feed it.

Your customer list. Your top 50 buyers and what they actually said in interviews. Your brand voice doc. Your SOPs. Your ranked ICP. Your purchase history segmented by LTV. The hundred little named things about your business that nobody outside your business knows.

Feed that to AI and the output is wildly different than what your competitor gets. Feed nothing to AI, and your output is interchangeable with everyone else's.

The work isn't writing better prompts. The work is building better inputs.

## What "better inputs" actually looks like

A bad input is a vibe. "Write in our brand voice." Cool, what's the brand voice. Where's the doc. Did the AI ever see it.

A good input is a SKILLS.md file the AI loads every time. The brand voice rules, with examples of what to do and what not to do. The named ICP with the language they use. The product positioning with the three key messages and the proof points behind each one. The list of competitors and how you're different. The do-not-use word list.

A bad input is your customer list as a CSV with name and email. A good input is your customer list enriched with role, company size, LTV bucket, channel they came in through, what they bought first, what they bought second, what they said in their last support ticket.

A bad input is "make me an ad." A good input is "we just identified 127 agency owners with 5 to 20 employees who are spending 30% more than other segments. They're bundling tools at 2.3x last quarter's rate. Write three headline variants for a landing page targeting them, using the agency-owner voice doc, with proof from the bundle data."

Same model. Same prompt structure. Wildly different output, because the input has actual signal.

## You're building this for the team, not just yourself

The other thing that gets missed. You're not just building inputs for you. You're building them for your whole team.

If you're a CMO or a VP of marketing, the dream isn't that you can prompt AI well. The dream is that anyone on your team can prompt AI well, because the inputs are standardized and accessible.

The brand voice file lives in the repo. The ICP file lives in the repo. The product positioning lives in the repo. Anyone on the team, with any AI agent, gets consistent on-brand output, because they're all pulling from the same inputs.

This is the operational unlock that turns AI from a personal productivity hack into a team multiplier. The work isn't training the team to use AI. The work is building the inputs once, well, so the team's AI use is consistent.

## Always be on the quest for better inputs

Here's the operating principle.

Better inputs make better outputs. Better outputs make better marketing. Better marketing makes better customers. Better customers give you more signal. More signal makes better inputs.

That loop, run for two years, makes you uncatchable. The brand that knows its customers cold and feeds that knowledge to AI every day pulls away from the brand that's still treating AI like a writing assistant.

You should always be hunting for better inputs. More customer interviews from your top 50 buyers. Cleaner data on what actually drives LTV. Tighter brand voice docs. Better segmented lists. Better SOPs.

This is what we're building with ICPIntel. The input layer for AI marketing. Take your customer list, enrich it, identify the 10% who matter, surface the language they use, and ship a SKILLS.md file your team can use with any AI tool.

We're not selling AI. We're selling the inputs that make AI worth using.

Garbage in, garbage out is older than ChatGPT. The teams that take it seriously are about to leave everyone else behind.`,
  },
];

export default posts;

export function getPostBySlug(slug) {
  return posts.find((p) => p.slug === slug);
}

export function getRelatedPosts(slug, category) {
  return posts.filter((p) => p.slug !== slug && p.category === category).slice(0, 2);
}

export function getPostsByCategory(category) {
  if (!category) return posts;
  return posts.filter((p) => p.category === category);
}
